import React, { useState, useEffect, useRef } from "react";
import { useStyles } from "./style";
import { Form, Button, Input, Container, Header, CheckboxGroup, Checkbox, Toggle, Whisper, Tooltip } from "rsuite";

import MarvinEditIcon from "../../../../../Icons/Marvin/marvinEditIcon.svg";
import { ReactComponent as MarvinTooltip } from "../../../../../Icons/Marvin/tooltip.svg";
import { ReactComponent as SpeechToTextIcon} from "../../../../../Icons/Marvin/speechToText.svg";
import { getDevice } from "../../../IFormDesignerLogic";

import MarvinLoader from "../MarvinLoader";
import useSpeechRecognition from "./useSpeechRecognition";
import { Tooltip as CustomTooltip } from "@material-ui/core";

const { ControlLabel, Group } = Form;

const ControlLabelGroup = ({ classes, item, index }) => (
  <ControlLabel className={classes.label}>
    {item.label || `label ${index}`}
    {item?.labelOptional && <span className={classes.labelOptional}>{item?.labelOptional}</span>}
    {item.mandatory && <span className={classes.mandatoryAstrik}>*</span>}

    {item.tooltip && (
      <Whisper placement="bottom" trigger="hover" speaker={<Tooltip>{item.tooltip}</Tooltip>}>
        <MarvinTooltip className={classes.tooltipIcon} />
      </Whisper>
    )}
  </ControlLabel>
);

const MarvinInputForm = (props) => {
  const { isBrowserCompatible, isListening, transcript, toggleListen, setIsListening, isSecureProtocol } = useSpeechRecognition({
    onEndSpeechRecognition: () => console.log("Speech recognition ended")
  });
  const { formDetails = {}, inputField = [], states = {}, callbackFn } = props;
  const { marvinRequestSent } = states;
  const isTablet = getDevice("tablet") === "tablet";
  const isMobile = getDevice("mobile") === "mobile";
  const classes = useStyles({ marvinRequestSent,isListening,isTablet,isMobile });
  const [formData, setFormData] = useState(formDetails.values);
  const [shouldEditName, setShouldEditName] = useState(false);
  const [appendSpeechData,setAppendSpeechData]= useState(false)
  const tempAdditionalCommentRef = useRef(formData?.additionalComment);

  const handleChange = (value, name) => {
    let checkboxStyle3Array = [];

    if (name === "additionalOptions") {
      checkboxStyle3Array = formData?.[name];
      if (!checkboxStyle3Array?.includes(value)) checkboxStyle3Array.push(value);
      else {
        const index = checkboxStyle3Array.indexOf(value);
        if (index !== -1) {
          checkboxStyle3Array.splice(index, 1);
        }
      }
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: name === "additionalOptions" ? checkboxStyle3Array : value
    }));
  };

  const handleClick = (allow,click) => {
    if (isSecureProtocol || allow) {
      toggleListen();
      setAppendSpeechData(true);
      if(!isListening && click){
        if(document.getElementById("speechDiv")){
          document.getElementById("speechDiv").focus();
          document.getElementById("speechDiv").style.cssText = "border: none !important; outline: none !important;";
        }
      }
    } else {
      props.showAlertConfirmNotifications({
        open: true,
        showConfirm: "showNotification",
        alertType: "error",
        autoHideDuration: 5000,
        alertContentKey: `secureNetworkCompatibilityMsg`
      });
    }
  };

  useEffect(() => {    
    if (isListening) {      
      if(appendSpeechData){
        tempAdditionalCommentRef.current = formData?.additionalComment || "";       
        setAppendSpeechData(false)
      }
      handleChange(tempAdditionalCommentRef?.current+transcript, "additionalComment");
    }
    
  }, [transcript]);
  const handleNameEdit = () => {
    if (!shouldEditName) {
      setShouldEditName(true);
    }
  };

  const handleSubmit = (event, e) => {
    if (callbackFn) {
      if(event === true) setShouldEditName(false);
      const action = event === true ? (formDetails.showRegenerateButton ? "addmorefields" : "generate") : event;
      callbackFn(formData, action, e);
    }
  };

  return (
    <Container className={["rsuite_component_parent", classes.formContainer].join(" ")}>
      {formDetails.heading ? (
        <Header className={classes.header}>
          <p className={classes.heading}>{formDetails.heading}</p>
        </Header>
      ) : null}

      <Form className={classes.form}>
        {inputField.map((item, index) => {
          const type = item.type || "";
          switch (type.toLowerCase()) {
            case "textbox":
              return (
                <Group key={item.label} className={classes.formGroup}>
                  <div className={classes.labelContainer}>
                    <ControlLabelGroup classes={classes} item={item} index={index} />

                    {item.editable && !shouldEditName && ( 
                      <img
                        src={MarvinEditIcon}
                        alt="marvin Edit Icon"
                        className={classes.nameEditIcon}
                        onClick={handleNameEdit}
                      />)
                    }
                  </div>

                  <Input
                    id="marvinInput"
                    name={item.label || `label ${index}`}
                    placeholder={item.Placeholder || `label ${index}`}
                    required={item.mandatory}
                    value={formData[item.field || item.label]}
                    onChange={(value) => handleChange(value, item.field || item.label)}
                    className={[classes.input, classes.textfield].join(" ")}
                    disabled={
                      item.editable
                        ? shouldEditName
                          ? false
                          : item.disabled || marvinRequestSent
                        : item.disabled || marvinRequestSent
                    }
                  />
                </Group>
              );

            case "textarea":
              return (
                <Group key={item.label} className={[classes.formGroup, classes.textareaPosition].join(" ")}>
                  <ControlLabelGroup classes={classes} item={item} index={index} />

                  <Input
                    id="marvinInput"
                    as="textarea"
                    rows={4}
                    name={item.label || `label ${index}`}
                    required={item.mandatory}
                    placeholder={isListening ? item?.PlaceholderListening : item.Placeholder || `label ${index}`}
                    value={formData[item.field || item.label]}
                    onChange={(value) => handleChange(value, item.field || item.label)}
                    className={[classes.input, classes.textarea].join(" ")}
                    disabled={item.disabled || marvinRequestSent}
                  />

                  <div
                    className={[classes.speechToText, `${isListening ? classes.speechToTextListening : null}`].join(
                      " "
                    )}
                    id="speechDiv"
                    tabIndex="0"
                    style={{pointerEvents:(item.disabled || marvinRequestSent) ? "none" : null}}
                    onClick={() => {
                      handleClick(undefined,"click");                      
                    }}
                    onBlur={() => {
                      if(isListening){
                        handleClick();
                      }
                    }}
                  >
                    <SpeechToTextIcon className={classes.speechToTextImg} />
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <span
                      className={[
                        classes.clearInput,
                        `${formData[item.field || item.label] ? classes.clearInputEnabled : classes.clearInputDisabled}`
                      ].join(" ")}
                      onClick={() => {
                        handleChange("", item.field || item.label)
                        if(isListening){
                          handleClick("allow");
                        }
                      }}
                    >
                      {"Clear input"}
                    </span>
                  </div>
                </Group>
              );

            case "combo":
              return (
                <Group key={item.label} className={classes.formGroup}>
                  <ControlLabelGroup classes={classes} item={item} index={index} />

                  <select
                    className={[classes.input, classes.combo].join(" ")}
                    onChange={(e) => handleChange(e.target.value, item.field || item.label)}
                    disabled={item.disabled || marvinRequestSent}
                    required={item.mandatory}
                  >
                    <option value={""} selected={formData[item.field || item.label] === ""}>
                      {`Select ${item.label}`}
                    </option>

                    {item.options?.map((option) => (
                      <option
                        value={option.value}
                        key={option.label}
                        selected={option.value === formData[item.field || item.label]}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Group>
              );

            case "checkbox":
              return (
                <Group key={item.label} className={classes.formGroup}>
                  <ControlLabelGroup classes={classes} item={item} index={index} />

                  <CheckboxGroup
                    name={item.label || `label ${index}`}
                    inline={item.inline}
                    required={item.mandatory}
                    value={formData[item.field || item.label]}
                    onChange={(value) => handleChange(value, item.field || item.label)}
                    disabled={item.disabled || marvinRequestSent}
                  >
                    {item.options?.map((option) => (
                      <Checkbox
                        value={option.value}
                        key={option.label}
                        // selected={option.value === formData[item.field || item.label]}
                      >
                        {option.label}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </Group>
              );

            case "checkboxstyle3":
              return (
                <Group key={item.label} className={classes.formGroup}>
                  <ControlLabelGroup classes={classes} item={item} index={index} />
                  <div className={classes.checkboxStyle3Container}>
                    {item.options?.map((option) => (
                      <CustomTooltip
                      title={option?.tooltip}
                      classes={{
                        tooltip: classes.tooltip,
                        arrow: classes.arrow
                      }}
                      placement={"bottom-start"}
                      arrow
                    >
                      <div
                        key={option.label}
                        className={[
                          classes.checkboxStyle3Div,
                          formData?.[item.field || item.label]?.includes(option?.value)
                            ? classes.checkboxStyle3DivSelected
                            : null,
                          item.disabled || marvinRequestSent ? classes.checkboxStyle3DivDisabled : null,
                          (item.disabled || marvinRequestSent) &&
                          formData?.[item.field || item.label]?.includes(option?.value)
                            ? classes.checkboxStyle3DivSelectedDisabled
                            : null
                        ].join(" ")}
                        onClick={() =>
                          !(item.disabled || marvinRequestSent) && handleChange(option.value, item.field || item.label)
                        }
                      >
                        {option.label}
                      </div>
                      </CustomTooltip>
                    ))}
                  </div>
                </Group>
              );

            case "toggle":
              return (
                <Group key={item.label} className={[classes.formGroup, classes.toggleGroup].join(" ")}>
                  <ControlLabelGroup classes={classes} item={item} index={index} />
                  <Toggle
                    size="sm"
                    color="#0072C6"
                    disabled={item.disabled || marvinRequestSent}
                    onChange={(value) => handleChange(value, item.field || item.label)}
                    checked={formData[item.field || item.label]}
                  />
                </Group>
              );
            default:
              return <div className="noType"></div>;
          }
        })}

        <Group className={[classes.formGroup, classes.footer].join(" ")} style={{gap:"unset"}}>
          <div style={{display:"flex",flexDirection:"row",width:"100%",padding: "11px 16px"}}>
          {marvinRequestSent && <MarvinLoader />}
          <div className={classes.buttonGroup}>
            {marvinRequestSent ? (
              <Button
                type="button"
                appearance="secondary"
                className={[classes.button, classes.secondaryButton].join(" ")}
                onClick={(e) => handleSubmit("stop", e)}
              >
                {formDetails.stopButtonLabel || "Stop Regenerating"}
              </Button>
            ) : (
              <>
                {formDetails.showCancel ? (
                  <Button
                    type="button"
                    appearance="secondary"
                    className={[classes.button, classes.secondaryButton].join(" ")}
                    onClick={(e) => handleSubmit("cancel", e)}
                  >
                    {formDetails.cancelButtonLabel || "Cancel"}
                  </Button>
                ) : null}

                {formDetails.showRegenerateButton ? (
                  <Button
                    type="submit"
                    appearance="secondary"
                    className={[classes.button1, classes.regenerateButton].join(" ")}
                    onClick={(e) => handleSubmit(true, e)}
                  ></Button>
                ) : (
                  <Button
                    type="submit"
                    appearance="primary"
                    className={[classes.button, classes.primaryButton].join(" ")}
                    onClick={(e) => handleSubmit(true, e)}
                  >
                    {formDetails.submitButtonLabel}
                  </Button>
                )}
              </>
            )}
          </div>
          </div>
          <div className={classes.footerText}>
          Created by Gen AI. Verify all UI elements.
          </div>
        </Group>
      </Form>
    </Container>
  );
};

export default MarvinInputForm;
